import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Truck } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import AuthImage from "@/components/AuthImage";
import { toast } from "sonner";

const EMPTY = { razao_social: "", nome_fantasia: "", documento: "", contato: "", telefone: "", email: "", observacoes: "", logo_path: null, ativo: true };

export default function Fornecedores() {
  const { user } = useAuth();
  const isAdmin = ["SUPER_ADMIN", "MASTER"].includes(user.role);
  const [rows, setRows] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/fornecedores").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const open = (r) => { r ? (setForm({ ...EMPTY, ...r }), setEditId(r.id)) : (setForm(EMPTY), setEditId(null)); setModal(true); };

  const save = async () => {
    if (!form.razao_social.trim()) return toast.error("Razão Social obrigatória");
    setSaving(true);
    try {
      if (editId) await api.put(`/fornecedores/${editId}`, form);
      else await api.post("/fornecedores", form);
      toast.success(editId ? "Fornecedor atualizado" : "Fornecedor criado");
      setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setSaving(false); }
  };
  const toggle = async (r) => { try { await api.put(`/fornecedores/${r.id}`, { ...r, ativo: !r.ativo }); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erro"); } };
  const del = async (r) => {
    if (!window.confirm(`Excluir "${r.razao_social}"? Esta ação não pode ser desfeita.`)) return;
    try { await api.delete(`/fornecedores/${r.id}`); toast.success("Fornecedor excluído"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-5" data-testid="fornecedores-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Cadastros</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Truck className="w-8 h-8 text-primary" /> Fornecedores</h1>
        </div>
        <Button onClick={() => open(null)} data-testid="btn-novo-fornecedor"><Plus className="w-4 h-4" /> Novo Fornecedor</Button>
      </div>

      {!rows ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="grid gap-3">
          {rows.map((r) => (
            <Card key={r.id} className="bg-card/70 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`fornecedor-${r.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-11 h-11 rounded-md bg-secondary flex items-center justify-center shrink-0 overflow-hidden">
                  {r.logo_path
                    ? <AuthImage path={r.logo_path} alt={r.razao_social} className="w-full h-full object-cover" />
                    : <Truck className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><span className="font-semibold">{r.nome_fantasia || r.razao_social}</span>
                    {!r.ativo && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-300 border border-red-500/40 font-mono">Inativo</span>}</div>
                  <div className="text-xs text-muted-foreground">{r.razao_social} · {r.documento || "sem doc"} · {r.telefone || "sem tel"} · {r.email || ""}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Switch checked={r.ativo} onCheckedChange={() => toggle(r)} data-testid={`forn-toggle-${r.id}`} />
                <Button variant="outline" size="sm" onClick={() => open(r)} data-testid={`forn-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                {isAdmin && <Button variant="outline" size="sm" className="text-red-400" onClick={() => del(r)} data-testid={`forn-del-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            </Card>
          ))}
          {!rows.length && <Card className="p-10 text-center text-muted-foreground">Nenhum fornecedor cadastrado.</Card>}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">{editId ? "Editar" : "Novo"} Fornecedor</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Razão Social</Label><Input value={form.razao_social} onChange={(e) => set("razao_social", e.target.value)} data-testid="forn-razao" /></div>
            <div><Label>Nome Fantasia</Label><Input value={form.nome_fantasia} onChange={(e) => set("nome_fantasia", e.target.value)} data-testid="forn-fantasia" /></div>
            <div><Label>CNPJ / CPF</Label><Input value={form.documento} onChange={(e) => set("documento", e.target.value)} data-testid="forn-doc" /></div>
            <div><Label>Contato</Label><Input value={form.contato} onChange={(e) => set("contato", e.target.value)} data-testid="forn-contato" /></div>
            <div><Label>Telefone / WhatsApp</Label><Input value={form.telefone} onChange={(e) => set("telefone", e.target.value)} data-testid="forn-tel" /></div>
            <div className="sm:col-span-2"><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="forn-email" /></div>
            <div className="sm:col-span-2"><ImageUpload label="Logo do fornecedor" value={form.logo_path} onChange={(p) => set("logo_path", p)} testid="forn-logo" /></div>
            <div className="sm:col-span-2"><Label>Observações</Label><Input value={form.observacoes} onChange={(e) => set("observacoes", e.target.value)} data-testid="forn-obs" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-salvar-fornecedor">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
